import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import referentesService from "../../../../services/referentesService";
import { listarCursos } from "../../../../services/cursosService";
import { showToast } from "../../../../utils/showToast";

const TIPO_LABEL = { comisionista: "Comisionista", externo: "Externo", estudiante: "Estudiante", docente: "Docente", administrativo: "Administrativo" };

const ESTADO_BADGE = {
  pendiente:  { cls: "bg-warning-subtle text-warning",     label: "Pendiente"  },
  no_pagada:  { cls: "bg-danger-subtle text-danger",       label: "No cobrada" },
  pagada:     { cls: "bg-success-subtle text-success",     label: "Cobrada"    },
  anulada:    { cls: "bg-secondary-subtle text-secondary", label: "Anulada"    },
};

export default function ComisionistaPerfilView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [referente, setReferente]       = useState(null);
  const [bonificaciones, setBonif]      = useState([]);
  const [cursosComision, setCursosComision] = useState([]);
  const [cursos, setCursos]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);

  // estado modal nueva comisión por curso
  const [showAddComision, setShowAddComision] = useState(false);
  const [formComision, setFormComision]       = useState({ curso_id: "", monto: "" });
  const [savingComision, setSavingComision]   = useState(false);

  const pageSize   = 10;
  const totalPages = Math.max(1, Math.ceil(bonificaciones.length / pageSize));
  const bonifPag   = useMemo(
    () => bonificaciones.slice((page - 1) * pageSize, page * pageSize),
    [bonificaciones, page]
  );
  const goToPage = (n) => setPage(Math.min(Math.max(1, n), totalPages));

  useEffect(() => {
    const cargar = async () => {
      try {
        const [ref, bonif, comisiones, listaCursos] = await Promise.all([
          referentesService.obtener(id),
          referentesService.obtenerBonificaciones(id),
          referentesService.listarCursosComision(id),
          listarCursos(),
        ]);
        setReferente(ref);
        setBonif(Array.isArray(bonif) ? bonif : []);
        setCursosComision(Array.isArray(comisiones) ? comisiones : []);
        setCursos(Array.isArray(listaCursos) ? listaCursos : []);
      } catch {
        showToast({ type: "danger", message: "Error al cargar el perfil." });
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [id]);

  const handleAgregarComision = async (e) => {
    e.preventDefault();
    if (!formComision.curso_id || formComision.monto === "") {
      return showToast({ type: "warning", message: "Selecciona un curso e ingresa el monto." });
    }
    const monto = parseFloat(formComision.monto);
    if (isNaN(monto) || monto < 0) {
      return showToast({ type: "warning", message: "El monto debe ser un número positivo." });
    }
    setSavingComision(true);
    try {
      const nueva = await referentesService.crearCursoComision(id, { curso_id: Number(formComision.curso_id), monto });
      setCursosComision((prev) => {
        const sin = prev.filter((c) => c.curso_id !== nueva.curso_id);
        return [...sin, nueva].sort((a, b) => a.curso_nombre.localeCompare(b.curso_nombre, "es"));
      });
      setFormComision({ curso_id: "", monto: "" });
      setShowAddComision(false);
      showToast({ type: "success", message: "Comisión guardada." });
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "Error al guardar." });
    } finally {
      setSavingComision(false);
    }
  };

  const handleEliminarComision = async (cursoId) => {
    try {
      await referentesService.eliminarCursoComision(id, cursoId);
      setCursosComision((prev) => prev.filter((c) => c.curso_id !== cursoId));
      showToast({ type: "success", message: "Comisión eliminada." });
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "Error al eliminar." });
    }
  };

  if (loading) return <div className="p-4 text-muted">Cargando...</div>;
  if (!referente) return <div className="p-4 text-danger">Comisionista no encontrado.</div>;

  const totales = bonificaciones.reduce(
    (acc, b) => {
      const monto = parseFloat(b.monto_neto ?? b.monto ?? 0);
      if (b.estado === "pagada")    acc.cobrado   += monto;
      if (b.estado === "no_pagada") acc.pendiente += monto;
      if (b.estado === "anulada")   acc.anulado   += monto;
      acc.generado += b.estado !== "anulada" ? monto : 0;
      return acc;
    },
    { cobrado: 0, pendiente: 0, anulado: 0, generado: 0 }
  );

  const cursosYaConfigurados = new Set(cursosComision.map((c) => c.curso_id));
  const cursosDisponibles    = cursos.filter((c) => !cursosYaConfigurados.has(c.id));

  return (
    <section className="management-page management-theme-teal">
      <div className="container-fluid">
        {/* Header */}
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-start flex-wrap gap-2 w-100">
            <div className="flex-grow-1">
              <button className="btn btn-link p-0 text-muted text-decoration-none small mb-1"
                onClick={() => navigate(-1)}>
                <i className="bi bi-chevron-left me-1"></i>Comisionistas
              </button>
              <div className="dashboard-eyebrow">Financiero / Comisionistas</div>
              <h5 className="mb-0">{referente.nombre}</h5>
              <span className="badge rounded-pill bg-primary-subtle text-primary mt-1">
                {TIPO_LABEL[referente.tipo] || referente.tipo}
              </span>
            </div>
          </div>
        </div>

        {/* Datos + totales */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h6 className="fw-semibold mb-3"><i className="bi bi-person me-2"></i>Datos personales</h6>
                <dl className="mb-0">
                  {referente.identificacion && <><dt className="text-muted small">Identificación</dt><dd>{referente.identificacion}</dd></>}
                  {referente.telefono       && <><dt className="text-muted small">Teléfono</dt><dd>{referente.telefono}</dd></>}
                  {referente.email          && <><dt className="text-muted small">Email</dt><dd>{referente.email}</dd></>}
                  <dt className="text-muted small">Estado</dt>
                  <dd>
                    <span className={`badge rounded-pill ${referente.activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>
                      {referente.activo ? "Activo" : "Inactivo"}
                    </span>
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-8">
            <div className="row g-3 h-100">
              {[
                { label: "Total generado",  value: totales.generado,  icon: "bi-coin",            color: "primary"   },
                { label: "Cobrado",         value: totales.cobrado,   icon: "bi-check-circle",    color: "success"   },
                { label: "Pendiente",       value: totales.pendiente, icon: "bi-hourglass-split", color: "warning"   },
                { label: "Anulado",         value: totales.anulado,   icon: "bi-x-circle",        color: "secondary" },
              ].map((t) => (
                <div className="col-6" key={t.label}>
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body p-3 d-flex align-items-center gap-3">
                      <div className={`config-card-icon text-${t.color}`} style={{ background: `var(--bs-${t.color}-bg-subtle)` }}>
                        <i className={`bi ${t.icon}`}></i>
                      </div>
                      <div>
                        <div className="text-muted small">{t.label}</div>
                        <div className="fw-bold fs-5">${t.value.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comisiones por curso */}
        <div className="management-card p-0 mb-4">
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-semibold">
              <i className="bi bi-currency-dollar me-2"></i>Comisiones por curso ({cursosComision.length})
            </h6>
            <button className="btn btn-sm management-btn-primary" onClick={() => setShowAddComision(true)}>
              <i className="bi bi-plus me-1"></i>Agregar
            </button>
          </div>

          {showAddComision && (
            <div className="p-3 border-bottom bg-light">
              <form className="row g-2 align-items-end" onSubmit={handleAgregarComision}>
                <div className="col-12 col-md-6">
                  <label className="form-label form-label-sm mb-1">Curso</label>
                  <select className="form-select form-select-sm" value={formComision.curso_id}
                    onChange={(e) => setFormComision((f) => ({ ...f, curso_id: e.target.value }))}>
                    <option value="">Seleccionar...</option>
                    {cursosDisponibles.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre} ({c.codigo_curso})</option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label form-label-sm mb-1">Monto ($)</label>
                  <input type="number" min="0" step="0.01" className="form-control form-control-sm"
                    placeholder="Ej. 20.00" value={formComision.monto}
                    onChange={(e) => setFormComision((f) => ({ ...f, monto: e.target.value }))} />
                </div>
                <div className="col-6 col-md-3 d-flex gap-2">
                  <button type="submit" className="btn btn-sm management-btn-primary flex-grow-1" disabled={savingComision}>
                    {savingComision ? "..." : "Guardar"}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setShowAddComision(false); setFormComision({ curso_id: "", monto: "" }); }}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          {cursosComision.length === 0 ? (
            <div className="p-4 text-muted text-center small">
              Sin comisiones individuales — aplica el monto definido por curso o el global.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Curso</th>
                    <th>Código</th>
                    <th className="text-end">Comisión</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {cursosComision.map((c) => (
                    <tr key={c.curso_id}>
                      <td>{c.curso_nombre}</td>
                      <td className="text-muted small">{c.codigo_curso}</td>
                      <td className="text-end fw-semibold">${parseFloat(c.monto).toFixed(2)}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-danger py-0 px-1"
                          onClick={() => handleEliminarComision(c.curso_id)}
                          title="Eliminar override">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Historial de bonificaciones */}
        <div className="management-card p-0">
          <div className="p-3 border-bottom">
            <h6 className="mb-0 fw-semibold">
              <i className="bi bi-list-ul me-2"></i>Historial de bonificaciones ({bonificaciones.length})
            </h6>
          </div>
          {bonificaciones.length === 0 ? (
            <div className="p-4 text-muted text-center">Sin bonificaciones registradas.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Estudiante referido</th>
                    <th>Matrícula</th>
                    <th className="text-end">Monto</th>
                    <th>Estado</th>
                    <th>Fecha generación</th>
                  </tr>
                </thead>
                <tbody>
                  {bonifPag.map((b, i) => {
                    const globalIndex = (page - 1) * pageSize + i;
                    const badge = ESTADO_BADGE[b.estado] || { cls: "bg-secondary-subtle text-secondary", label: b.estado };
                    return (
                      <tr key={b.id}>
                        <td className="text-muted">{globalIndex + 1}</td>
                        <td>
                          <div>{b.estudiante_referido_nombre || "-"}</div>
                          {b.estudiante_referido_documento && <div className="text-muted small">{b.estudiante_referido_documento}</div>}
                        </td>
                        <td>{b.codigo_matricula || "-"}</td>
                        <td className="text-end">${parseFloat(b.monto_neto ?? b.monto ?? 0).toFixed(2)}</td>
                        <td><span className={`badge rounded-pill ${badge.cls}`}>{badge.label}</span></td>
                        <td className="text-muted small">
                          {b.fecha_generacion ? new Date(b.fecha_generacion).toLocaleDateString("es-EC") : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {bonificaciones.length > pageSize && (
            <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="text-muted small">
                Mostrando {(page - 1) * pageSize + 1}–{Math.min(bonificaciones.length, page * pageSize)} de {bonificaciones.length}
              </div>
              <div className="btn-group">
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="btn btn-outline-secondary btn-sm" disabled>
                  {page} / {totalPages}
                </button>
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
